import { DisplayPlatformPrettyId } from 'typeDeclarations/graphql/nodes';

import {
  NUM_MAX_CALLOUT_EXTENSIONS_BING,
  NUM_MAX_LOCATION_EXTENSIONS_BING,
  NUM_MAX_SITELINK_EXTENSIONS_BING,
  NUM_MAX_SNIPPET_EXTENSIONS_BING,
} from '../BingAd/BingAdForm/types';

import {
  NUM_MAX_CALLOUT_EXTENSIONS_GOOGLE_DYNAMIC,
  NUM_MAX_LOCATION_EXTENSIONS_GOOGLE_DYNAMIC,
  NUM_MAX_SITELINK_EXTENSIONS_GOOGLE_DYNAMIC,
  NUM_MAX_SNIPPET_EXTENSIONS_GOOGLE_DYNAMIC,
} from '../GoogleDynamicSearchAd/GoogleDynamicSearchAdForm/types';

import {
  NUM_MAX_CALLOUT_EXTENSIONS_GOOGLE_TEXT,
  NUM_MAX_LOCATION_EXTENSIONS_GOOGLE_TEXT,
  NUM_MAX_SITELINK_EXTENSIONS_GOOGLE_TEXT,
  NUM_MAX_SNIPPET_EXTENSIONS_GOOGLE_TEXT,
} from '../GoogleTextAd/GoogleTextAdForm/types';

import { AdTypeId, AdTypename } from './types';

import { Feature } from 'featureFlags/constants';

export const NUM_MAX_CALLOUT_EXTENSIONS = Math.max(
  NUM_MAX_CALLOUT_EXTENSIONS_BING,
  NUM_MAX_CALLOUT_EXTENSIONS_GOOGLE_TEXT,
  NUM_MAX_CALLOUT_EXTENSIONS_GOOGLE_DYNAMIC,
);

export const NUM_MAX_SNIPPET_EXTENSIONS = Math.max(
  NUM_MAX_SNIPPET_EXTENSIONS_BING,
  NUM_MAX_SNIPPET_EXTENSIONS_GOOGLE_TEXT,
  NUM_MAX_SNIPPET_EXTENSIONS_GOOGLE_DYNAMIC,
);

export const NUM_MAX_LOCATION_EXTENSIONS = Math.max(
  NUM_MAX_LOCATION_EXTENSIONS_BING,
  NUM_MAX_LOCATION_EXTENSIONS_GOOGLE_TEXT,
  NUM_MAX_LOCATION_EXTENSIONS_GOOGLE_DYNAMIC,
);

export const NUM_MAX_SITELINK_EXTENSIONS = Math.max(
  NUM_MAX_SITELINK_EXTENSIONS_BING,
  NUM_MAX_SITELINK_EXTENSIONS_GOOGLE_TEXT,
  NUM_MAX_SITELINK_EXTENSIONS_GOOGLE_DYNAMIC,
);

export const ADS_PAGE_SIZE = 6;
export const IMAGEFILES_PAGESIZE = 12;
export const NONE_OF_THESE = 'none-of-these';

export const SUPPORTED_ADVERTISING_PLATFORMS: DisplayPlatformPrettyId[] = [
  'bing',
  'google',
  'facebook',
  'instagram',
  'meta',
  'tiktok',
];

export const AD_PLATFORM_TO_AD_TYPENAMES_MAP: Record<DisplayPlatformPrettyId, AdTypename[]> = {
  bing: ['BingAdNode', 'BingResponsiveSearchAdNode'],
  facebook: ['FacebookAdNode', 'FacebookCarouselAdNode'],
  instagram: ['InstagramAdNode', 'InstagramCarouselAdNode'],
  google: [
    'GoogleTextAdNode',
    'GoogleResponsiveAdNode',
    'GoogleDynamicSearchAdNode',
    'GoogleResponsiveSearchAdNode',
    'GooglePMaxAssetGroupAdNode',
  ],
  meta: ['MetaVideoAdNode', 'MetaAdNode', 'MetaCarouselAdNode'],
  tiktok: ['TikTokCarouselAdNode', 'TikTokSingleVideoAdNode'],
};

export const AD_TYPE_TO_NAME: PartialRecord<AdTypename, string> = {
  BingAdNode: 'Text ad',
  FacebookAdNode: 'Image Ad',
  GoogleTextAdNode: 'Text ad',
  InstagramAdNode: 'Image Ad',
  InstagramCarouselAdNode: 'Carousel Ad',
  FacebookCarouselAdNode: 'Carousel Ad',
  GoogleResponsiveAdNode: 'Responsive ad',
  GoogleDynamicSearchAdNode: 'Dynamic search ad',
  BingResponsiveSearchAdNode: 'Responsive search ad',
  GoogleResponsiveSearchAdNode: 'Responsive search ad',
  MetaVideoAdNode: 'Video ad',
  MetaAdNode: 'Image Ad',
  MetaCarouselAdNode: 'Carousel Ad',
  GooglePMaxAssetGroupAdNode: 'PMAX Asset Group ad',
  TikTokCarouselAdNode: 'Carousel Ad',
  TikTokSingleVideoAdNode: 'Video Ad',
};

export const AD_TYPE_TO_DISPLAY_PLATFORM: PartialRecord<AdTypeId, DisplayPlatformPrettyId> = {
  'facebook+': 'facebook',
  'adwords+dynamic': 'google',
  'adwords+responsive': 'google',
  'bing+responsive_search': 'bing',
  'facebook+instagram': 'instagram',
  'adwords+responsive_search': 'google',
  'meta+': 'meta',
  'facebook+meta': 'meta',
  'adwords+pmax': 'google',
  'tiktok+': 'tiktok',
  'tiktok+carousel': 'tiktok',
  'tiktok+single_video': 'tiktok',
};

// used in AdTypeSelector and AdEditors to exclude ad types if beta feature exists and it's disabled
export const AD_TYPE_TO_FEATURE: PartialRecord<AdTypename, Feature> = {};

// used in AdPlatformsAccordion to exclude platforms if beta feature exists and it's disabled
export const DISPLAY_PLATFORM_TO_FEATURE: PartialRecord<DisplayPlatformPrettyId, Feature> = {};

/* Deprecated ad types */
export const DEPRECATED_AD_TYPES: PartialRecord<AdTypename, boolean> = {
  BingAdNode: true,
  GoogleTextAdNode: true,
};

export const VIDEO_ASPECT_RATIO_PORTRAIT_DEFAULT = '4:5'; // 0.5714 - It's a portrait default ratio (height > width)
export const VIDEO_ASPECT_RATIO_LANDSCAPE = '16:9'; // 1.7778 - It's a landscape ratio (width > height)
export const VIDEO_ASPECT_RATIO_PORTRAIT = '9:16'; // 0.5625 - It's a portrait ratio (height > width)
export const VIDEO_ASPECT_RATIO_SQUARE = '1:1'; // 1.0 - It's a square ratio (width = height)

export const TIKTOK_WIDTH = '320px';
export const TIKTOK_HEIGHT = '570px';
export const TIKTOK_MAGENTA = '#e23953';

export const printableIconSx = {
  color: 'white',
  '@media print': {
    color: 'grey.400',
  },
};

export const printableTextSx = {
  // 1. Force the base text to be white
  color: 'white',
  '@media print': {
    // 1. Force the base text to be white
    color: 'white',
    // 2. Mix with the background to create the perfect opposite color
    mixBlendMode: 'difference',
  },
};

export const printableButtonSx = {
  '@media print': {
    backgroundColor: 'white',
    borderColor: 'black',
    color: 'black',
  },
};
